// SIPP-729: Story Mode Screen 1 — WQI Overview (Figma redesign)
import { Box, Grid, Stack, Typography, useTheme } from '@mui/material'
import { useSelector } from 'react-redux'
import { UserSelector } from '../../../store/User'
import {
  WaterQualityIndexSelectors,
  UIStatus,
} from '../../../store/waterQualityIndex'
import { WaterTestKitResultsSelector } from '../../../store/waterTestKitResults'

const STATUS_DESCRIPTION: Record<UIStatus, string> = {
  unsafe: 'Your water needs immediate attention',
  cautious: 'Your water shows some areas of concern',
  safe: 'Your water quality is in good shape',
}

const STATUS_LABEL: Record<UIStatus, string> = {
  unsafe: 'Unsafe',
  cautious: 'Cautious',
  safe: 'Safe',
}

const QUESTIONS = [
  { emoji: '🩺', text: 'Is my water safe to drink?' },
  { emoji: '🏠', text: "Is it damaging my home's infrastructure?" },
  { emoji: '🚿', text: 'Is it causing problems with my skin, hair, or laundry?' },
  { emoji: '💧', text: 'Is it meeting my preferences for taste?' },
]

const statusPaletteKey = (status: UIStatus) =>
  status === 'unsafe' ? 'error' : status === 'cautious' ? 'warning' : 'success'

export const StoryScreen1 = () => {
  const theme = useTheme()
  const userAbbrev = useSelector(UserSelector.makeUserAbbreviated)
  const overall = useSelector(WaterQualityIndexSelectors.selectOverall)
  const safetyStatus = useSelector(WaterQualityIndexSelectors.selectWaterSafetyStatus)
  const utility = useSelector(WaterQualityIndexSelectors.selectUtility)
  const latestResult = useSelector(WaterTestKitResultsSelector.makeLatestResult)
  const summaryCounts = useSelector(WaterQualityIndexSelectors.selectSummaryCounts)

  const firstName = userAbbrev?.first_name || 'there'

  const overallStatus: UIStatus | null = (() => {
    if (!overall) return null
    if (safetyStatus === 'unsafe') return 'unsafe'
    if (safetyStatus === 'cautious') return 'cautious'
    return 'safe'
  })()

  // Derive test data status from latest result
  const testDataStatus: UIStatus | null = (() => {
    if (!latestResult?.result?.parameters) return null
    const params = latestResult.result.parameters
    const allItems = Object.values(params).flat() as any[]
    if (allItems.some((p) => p.label === 'High' || p.label === 'Low')) return 'unsafe'
    if (allItems.some((p) => p.label === 'Caution')) return 'cautious'
    return 'safe'
  })()

  // Derive utility status from compliance band
  const utilityStatus: UIStatus | null = (() => {
    if (!utility?.complianceSummary?.band) return null
    const b = utility.complianceSummary.band
    if (b === 'F' || b === 'Very High Risk' || b === 'High Risk') return 'unsafe'
    if (b === 'D' || b === 'Moderate Risk') return 'cautious'
    return 'safe'
  })()

  const pillRows: { label: string; status: UIStatus | null }[] = [
    { label: 'Signals', status: summaryCounts.total > 0 ? safetyStatus : null },
    { label: 'Test Data', status: testDataStatus },
    { label: 'Utility', status: utilityStatus },
  ]

  const getColor = (status: UIStatus) => theme.palette[statusPaletteKey(status)].main
  const getLightBg = (status: UIStatus) => theme.palette[statusPaletteKey(status)].light

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        px: 3,
        py: 4,
        gap: 2.5,
        '@keyframes fadeInUp': {
          from: { opacity: 0, transform: 'translateY(24px)' },
          to: { opacity: 1, transform: 'translateY(0)' },
        },
        animation: 'fadeInUp 0.5s ease-out both',
      }}
    >
      {/* 1. Title */}
      <Typography sx={{ fontWeight: 700, fontSize: 22, lineHeight: 1.3 }}>
        Your WQI Report is ready, {firstName}!
      </Typography>

      {/* 2. Subtitle */}
      <Typography sx={{ fontSize: 13, color: 'text.secondary', lineHeight: 1.5 }}>
        Your Water Quality Index Report is a personalized analysis of your tap water. It helps you
        understand what's in your water and what to do about it. We want you to be able to answer:
      </Typography>

      {/* 3. 2×2 Question Card Grid */}
      <Grid container spacing={1.5}>
        {QUESTIONS.map((q) => (
          <Grid size={{ xs: 6 }} key={q.text}>
            <Box
              sx={{
                bgcolor: 'background.paper',
                border: `1px solid ${theme.palette.grey[200]}`,
                borderRadius: '8px',
                p: 1.5,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: 0.75,
              }}
            >
              <Typography sx={{ fontSize: 20, lineHeight: 1 }}>{q.emoji}</Typography>
              <Typography sx={{ fontSize: 12, fontWeight: 500, lineHeight: 1.4 }}>
                {q.text}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>

      {/* 4. Overall Status Badge */}
      {overallStatus ? (
        <Box
          sx={{
            bgcolor: getLightBg(overallStatus),
            borderRadius: '12px',
            p: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          {/* Status icon */}
          <Box
            sx={{
              width: 48,
              height: 48,
              minWidth: 48,
              borderRadius: '50%',
              bgcolor: getColor(overallStatus),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography sx={{ fontWeight: 700, fontSize: 18, color: '#fff' }}>
              {overallStatus === 'unsafe' ? '!' : overallStatus === 'cautious' ? '~' : '✓'}
            </Typography>
          </Box>

          {/* Text */}
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 14, color: getColor(overallStatus) }}>
              Overall: {STATUS_LABEL[overallStatus]}
            </Typography>
            <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.25 }}>
              {STATUS_DESCRIPTION[overallStatus]}
            </Typography>
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            bgcolor: theme.palette.grey[100],
            borderRadius: '12px',
            p: 2,
          }}
        >
          <Typography sx={{ fontSize: 13, color: 'text.disabled' }}>
            Status not yet calculated
          </Typography>
        </Box>
      )}

      {/* 5. "Your Report at a Glance" caption */}
      <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.5 }}>
        Your Report at a Glance
      </Typography>

      {/* 6. Three status pill rows */}
      <Stack spacing={1}>
        {pillRows.map((row) => {
          const hasData = row.status !== null
          const dotColor = hasData ? getColor(row.status!) : theme.palette.grey[400]
          const bgColor = hasData ? getLightBg(row.status!) : theme.palette.grey[100]
          const textColor = hasData ? getColor(row.status!) : theme.palette.grey[500]
          const label = hasData ? `${row.label}: ${STATUS_LABEL[row.status!]}` : `${row.label}: No data yet`

          return (
            <Box
              key={row.label}
              sx={{
                bgcolor: bgColor,
                borderRadius: '20px',
                px: 2,
                py: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  minWidth: 8,
                  borderRadius: '50%',
                  bgcolor: dotColor,
                }}
              />
              <Typography sx={{ fontSize: 13, fontWeight: 500, color: textColor }}>
                {label}
              </Typography>
            </Box>
          )
        })}
      </Stack>
    </Box>
  )
}
